<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useLanguageStore } from '../stores/languageStore'
import { useAuthStore } from '../stores/auth'
import { getVideoId } from '../utils/youtube'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../config/supabase'
import { FormatService } from '../services/formatService'

interface Summary {
  id: string; // ID from user_summaries table
  summary_id: string; // ID from summaries table
  video_id: string;
  content: string;
  status: string;
  created_at: string;
  language: string;
  video_title: string;
  video_thumbnail: string;
  video_url: string;
  channel_name: string;
  channel_id: string;
  channel_language: string; // Channel's default language
  is_read: boolean;
  transcript?: string;
  tts_content?: string;
  tts_url?: string;
}

interface Channel {
  id: string;
  title: string;
  thumbnail_url: string;
  language?: string; // <-- Dil alanı eklendi
}

type VideoSummary = Summary;

const languageStore = useLanguageStore()
const authStore = useAuthStore()
const activeTab = ref('all') // all, today, week, month
const selectedLanguage = ref('all') // all, tr, en
const selectedChannel = ref('all') // all, veya kanal ID'si
const selectedSummary = ref<VideoSummary | null>(null)
const isMenuOpen = ref(false)
const error = ref('')

const summaries = ref<Summary[]>([])
const channels = ref<Channel[]>([])
const isLoading = ref(false)

const router = useRouter()
const route = useRoute()

// Ses dosyalarının URL'lerini saklayalım ki sonradan temizleyebilelim
const audioObjectUrls = ref<string[]>([]);

// Yeni değişkenler ekleyelim
const isLoadingTranscript = ref(false);

// Computed properties for filtered summaries
const filteredSummaries = computed(() => {
  if (!Array.isArray(summaries.value)) {
    console.warn('❗ Summaries is not an array:', summaries.value);
    return [];
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - today.getDay()) // Haftanın başlangıcı (Pazar)
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

  return summaries.value
    .filter(summary => {
      // Null veya undefined kontrolü
      if (!summary) return false;

      // First filter by date
      const summaryDate = new Date(summary.created_at)
      let dateMatches = true
      
      switch (activeTab.value) {
        case 'today':
          dateMatches = summaryDate >= today
          break
        case 'week':
          dateMatches = summaryDate >= weekStart
          break
        case 'month':
          dateMatches = summaryDate >= monthStart
          break
        default:
          dateMatches = true
      }

      // Then filter by language
      const languageMatches = selectedLanguage.value === 'all' || summary.language === selectedLanguage.value
      
      // Then filter by channel
      const channelMatches = selectedChannel.value === 'all' || summary.channel_id === selectedChannel.value

      return dateMatches && languageMatches && channelMatches
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) // En yeni özetler üstte
})

// Template için computed property
const hasNoSummaries = computed(() => {
  return filteredSummaries.value.length === 0
})

const fetchSummaries = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const token = await supabase.auth.getSession();
    // Kanal seçiliyse ve dili varsa, API'ye channelLanguage parametresiyle istek at
    let url = `${import.meta.env.VITE_API_URL}/api/summaries`;
    if (selectedChannel.value !== 'all') {
      // Öncelik: seçili dil (URL'den veya kullanıcıdan), yoksa kanalın dili
      let channelLanguage = selectedLanguage.value !== 'all' ? selectedLanguage.value : undefined;
      if (!channelLanguage) {
        const channel = channels.value.find(c => c.id === selectedChannel.value);
        if (channel && typeof (channel as any).language === 'string' && (channel as any).language) {
          channelLanguage = (channel as any).language;
        }
      }
      if (channelLanguage) {
        url += `?channelLanguage=${channelLanguage}`;
      }
    }
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token.data.session?.access_token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch summaries');
    }

    const data = await response.json();
    console.log('API Response:', data); // Debug log

    summaries.value = data.map((item: any) => {
      console.log('Processing summary item:', item); // Debug log
      console.log('Transcript from API:', item.transcript); // Debug log for transcript
      return {
        id: item.id,
        summary_id: item.summary_id,
        video_id: item.video_id,
        content: item.content,
        status: item.status,
        created_at: item.created_at,
        language: item.language,
        video_title: item.video_title,
        video_thumbnail: item.video_thumbnail,
        video_url: item.video_url,
        channel_name: item.channel_name,
        channel_id: item.channel_id,
        channel_language: item.channel_language,
        is_read: Boolean(item.is_read),
        transcript: item.transcript || null, // Ensure transcript is null if not present
        tts_content: item.tts_content,
        tts_url: item.tts_url
      };
    });

    console.log('Mapped summaries:', summaries.value); // Debug log
  } catch (err) {
    console.error('Error fetching summaries:', err);
    error.value = 'Failed to load summaries. Please try again later.';
  } finally {
    isLoading.value = false;
  }
};

// Kanal bilgilerini çekmek için yeni fonksiyon
const fetchChannels = async () => {
  try {
    const token = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/channels`, {
      headers: {
        'Authorization': `Bearer ${token.data.session?.access_token}`
      }
    });
    
    if (!response.ok) {
      console.error('Failed to fetch channels');
      return;
    }

    const data = await response.json();
    console.log('Channels API Response:', data);

    channels.value = data.map((item: any) => ({
      id: item.id,
      title: item.title,
      thumbnail_url: item.thumbnail_url,
      language: item.language // <-- Dil alanı eklendi
    }));

    console.log('Mapped channels:', channels.value);
  } catch (err) {
    console.error('Error fetching channels:', err);
  }
};

// Close menu when clicking outside
const closeMenu = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const profileButton = document.querySelector('.profile-button')
  const dropdownMenu = document.querySelector('.dropdown-menu')
  
  if (!profileButton?.contains(target) && !dropdownMenu?.contains(target)) {
    isMenuOpen.value = false
  }
}

onMounted(() => {
  console.log('Component mounted, loading summaries and channels...')
  console.log('Current language in SummariesView:', languageStore.currentLocale)
  
  // Query parametrelerinden channel ve language ayarla
  const channelIdFromRoute = route.query.channel_id as string;
  const languageFromRoute = route.query.language as string;
  if (channelIdFromRoute) {
    selectedChannel.value = channelIdFromRoute;
  }
  if (languageFromRoute) {
    selectedLanguage.value = languageFromRoute;
  }
  
  fetchSummaries()
  fetchChannels()
  document.addEventListener('click', closeMenu)
  
  // URL'den açılan özet olayını dinle
  document.addEventListener('open-summary', ((e: CustomEvent) => {
    console.log('📢 SummaryView - open-summary olayı yakalandı:', e.detail);
    if (e.detail?.summary) {
      handleSummaryClick(e.detail.summary);
    }
  }) as EventListener);

  // Hash parametresini algıla ve özeti otomatik aç
  const checkHashForSummary = () => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#id=')) {
      const summaryId = hash.substring(4); // "#id=" stringini kaldır
      console.log('📢 SummaryView - URL hash parametresinden özet ID algılandı:', summaryId);
      
      if (summaryId) {
        // Eğer özet yüklenmişse, özeti açmayı dene
        const summary = summaries.value.find(s => s.id === summaryId);
        if (summary) {
          console.log('📢 SummaryView - URL hash parametresinden özet açılıyor');
          handleSummaryClick(summary);
        } else {
          // Özet henüz yüklenmemiş, özet yüklendikten sonra tekrar kontrol et
          console.log('📢 SummaryView - Özet henüz yüklenmemiş, fetchSummaries sonrası tekrar denenecek');
          
          // Özetler yüklenince tekrar kontrol etmek için watch ekle
          const unwatch = watch(() => summaries.value, () => {
            const summary = summaries.value.find(s => s.id === summaryId);
            if (summary) {
              console.log('📢 SummaryView - Özetler yüklendi, URL hash parametresinden özet açılıyor');
              handleSummaryClick(summary);
              unwatch(); // İşlem başarılı olduğunda watch'u kaldır
            }
          });
        }
      }
    }
  };

  // Sayfa ilk yüklendiğinde hash kontrolü yap
  checkHashForSummary();
  
  // Hash değişikliklerini dinle
  window.addEventListener('hashchange', checkHashForSummary);
  
  // App.vue'dan gönderilen özel dil değişikliği eventlerini dinle
  const handlePreLanguageChange = (e: any) => {
    console.log('🔍 SummariesView - pre-language-change eventi alındı:', e.detail);
    // Dil değişikliği başlamadan önce yapılacak işlemler
    isLanguageChanging = true;
  };
  
  const handlePostLanguageChange = (e: any) => {
    console.log('🔍 SummariesView - post-language-change eventi alındı:', e.detail);
    // Dil değişikliğinden hemen sonra yapılacak işlemler
    forceRender.value++;
    console.log('🔍 SummariesView - forceRender değeri güncellendi:', forceRender.value);
    
    // İşlem tamamlandı
    setTimeout(() => {
      isLanguageChanging = false;
    }, 100);
  };
  
  document.addEventListener('pre-language-change', handlePreLanguageChange);
  document.addEventListener('post-language-change', handlePostLanguageChange);
  

  
  // Sayfa yenilemesini engellemek için önlemler - EN AGRESİF YÖNTEM
  
  // 1. Form submit ve sayfa geçişlerini önle
  const preventRefresh = (e: Event) => {
    console.log('Sayfa yenileme engelleniyor (event):', e.type);
    e.preventDefault();
    e.stopPropagation();
    return false;
  };
  
  // Sayfa içindeki tüm formları ve sayfa geçişlerini kontrol et
  document.querySelectorAll('form').forEach(form => {
    form.addEventListener('submit', preventRefresh);
  });
  
  // 2. Sayfa öncesi yenilenmesini engellemek için beforeunload event listener'ı ekle
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    // Sadece summaries sayfasındayken engelle
    if (window.location.pathname.includes('/summaries')) {
      console.log('Sayfa yenilenmesi engelleniyor (beforeunload)');
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
  };
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // 3. History API'yi override et
  const originalPushState = window.history.pushState;
  window.history.pushState = function() {
    console.log('History pushState engelleniyor:', arguments);
    return originalPushState.apply(this, arguments as any);
  };
  
  const originalReplaceState = window.history.replaceState;
  window.history.replaceState = function() {
    console.log('History replaceState engelleniyor:', arguments);
    return originalReplaceState.apply(this, arguments as any);
  };

  // UnMounted hook ekliyoruz
  onUnmounted(() => {
    document.removeEventListener('click', closeMenu);
    
    // Hash değişiklik listener'ını kaldır
    window.removeEventListener('hashchange', checkHashForSummary);
    
    // Özel eventleri kaldır
    document.removeEventListener('pre-language-change', handlePreLanguageChange);
    document.removeEventListener('post-language-change', handlePostLanguageChange);
    
    // Eklediğimiz event listener'ları kaldıralım
    window.removeEventListener('beforeunload', handleBeforeUnload);
    
    // Override edilen history API metotlarını geri yükle
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    
    // Formlardaki event listener'ları kaldır
    document.querySelectorAll('form').forEach(form => {
      form.removeEventListener('submit', preventRefresh);
    });
  });
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenu)
  
  // Özel olayı temizle
  document.removeEventListener('open-summary', ((e: CustomEvent) => {
    console.log('📢 SummaryView - open-summary olayı temizlendi');
    if (e.detail?.summary) {
      handleSummaryClick(e.detail.summary);
    }
  }) as EventListener);
  
  // Ses dosyaları ve URL'leri temizle
  cleanupAudio()
  
  // Audio elementlerini temizle
  ttsAudio.value = null
  backgroundAudio.value = null
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const truncateSummary = (text: string) => {
  if (!text) return '';
  
  // Temizleme işlemi yapmadan önce debug log
  console.log('Özet başlangıç karakterleri (temizleme öncesi):', text.slice(0, 10));
  
  // Tüm HTML etiketlerini temizleyerek düz metni al
  const plainText = text.replace(/<[^>]+>/g, '');
  
  // Encoded HTML karakterlerini temizle
  const cleanText = plainText.replace(/&lt;|&gt;|&amp;|&quot;|&#39;/g, '');
  
  // Debug log
  console.log('Özet başlangıç karakterleri (temizleme sonrası):', cleanText.slice(0, 10));
  
  // İçeriği temizle ve ilk birkaç cümleyi al
  const cleanedContent = cleanText.trim();
  const sentences = cleanedContent.split(/[.!?]+/).filter(s => s.trim().length > 0);
  let preview = sentences.slice(0, 2).join('. ');
  
  // Eğer çok uzunsa, kısalt
  if (preview.length > 150) {
    preview = preview.substring(0, 150) + '...';
  }
  
  // Son kez kontrol et - başında geçersiz karakter kalmasın
  preview = preview.replace(/^[\u0000-\u001F\u007F-\u009F\uFFFD]+/, '').trim();
  
  // Sadece metin döndür, emoji kullanma
  return `${preview}${sentences.length > 2 ? '...' : '.'}`;
};

const openSummary = (summary: Summary) => {
  console.log("Yeni bir özet açılıyor:", summary.id);
  
  // Önce mevcut modalı ve sesleri temizleyelim
  cleanupAudio();
  
  // Önceki özet ile yeni özet farklıysa, audio durumlarını tamamen sıfırla
  if (!selectedSummary.value || selectedSummary.value.id !== summary.id) {
    console.log("Farklı bir özet açılıyor, ses durumları tamamen sıfırlanıyor");
    ttsAudio.value = null;
    isPlaying.value = false;
    audioProgress.value = 0;
    currentTime.value = '0:00';
    totalDuration.value = '0:00';
    
    // Önceki URL'leri önbellekten temizle
    audioObjectUrls.value.forEach(url => {
      try {
        URL.revokeObjectURL(url);
        console.log('Audio URL önbellekten temizlendi:', url);
      } catch (err) {
        console.error('URL temizlenirken hata:', err);
      }
    });
    audioObjectUrls.value = [];
  }
  
  // Yeni özeti seç
  selectedSummary.value = summary;
  
  // Her zaman başlangıçta summary tab'ına geçiş yap
  activeModalTab.value = 'summary';
  console.log("Modal açıldı, aktif tab 'summary' olarak ayarlandı");
  
  // Özet okundu olarak işaretle
  if (!summary.is_read) {
    markSummaryAsRead(summary.id);
  }
  
  // Eğer transcript verisi yoksa arka planda yükleyelim
  if (!summary.transcript && summary.video_id) {
    console.log("Özet için transcript yükleniyor:", summary.id);
    fetchTranscript(summary.id, summary.video_id);
  }
}

// Sesleri ve önbelleği temizleyen yardımcı fonksiyon
const cleanupAudio = () => {
  // Ses dosyalarını durdur
  if (ttsAudio.value) {
    ttsAudio.value.pause();
    ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
    ttsAudio.value.removeEventListener('ended', handleTTSEnded);
    
    // Kaynağı temizle
    ttsAudio.value.src = '';
    ttsAudio.value.load(); // Tarayıcının kaynağı boşaltması için
  }
  
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
    backgroundAudio.value.currentTime = 0;
  }
  
  // Önbellekteki URL'leri serbest bırak
  audioObjectUrls.value.forEach(url => {
    try {
      URL.revokeObjectURL(url);
      console.log('Audio URL önbellekten temizlendi:', url);
    } catch (err) {
      console.error('URL temizlenirken hata:', err);
    }
  });
  
  // URL listesini temizle
  audioObjectUrls.value = [];
  
  isPlaying.value = false;
}

const closeSummary = () => {
  // Tüm ses ve önbellek kaynaklarını temizle
  cleanupAudio();
  
  // Modalı kapat
  selectedSummary.value = null;
}

const getEmbedUrl = (url: string): string | undefined => {
  const videoId = getVideoId(url)
  return videoId ? `https://www.youtube.com/embed/${videoId}` : undefined
}

const formattedSummary = computed(() => {
  if (!selectedSummary.value?.content) return '';
  
  let content = selectedSummary.value.content;
  
  // Özel karakter sorunlarının tespiti için debug log
  console.log('Ham metin (ilk 200 karakter):', content.slice(0, 200));
  
  // Sadece içeriği formatla
  const formattedContent = FormatService.formatSummaryText(content);
  
  // İşleme sonucunu göster
  console.log('İşlenmiş içerikten örnek:', formattedContent.slice(0, 200));
  
  // Sadece formatlı içeriği dön, kaynak footer'ı ekleme
  return formattedContent;
});

// HTML etiketlerini ve formatlama kodlarını kaldıran yardımcı fonksiyon
const stripHtml = (text: string): string => {
  if (!text) return '';
  
  // HTML etiketlerini temizle
  let plainText = text.replace(/<[^>]+>/g, '');
  
  // HTML özel karakterlerini çöz
  plainText = plainText.replace(/&lt;/g, '<');
  plainText = plainText.replace(/&gt;/g, '>');
  plainText = plainText.replace(/&amp;/g, '&');
  plainText = plainText.replace(/&quot;/g, '"');
  plainText = plainText.replace(/&#39;/g, "'");
  
  // Gereksiz satır başlarını ve fazla boşlukları temizle
  plainText = plainText.replace(/\n{3,}/g, '\n\n');
  plainText = plainText.replace(/\s{2,}/g, ' ').trim();
  
  return plainText;
};

const downloadSummary = () => {
  if (!selectedSummary.value) return;

  // HTML etiketlerini ve formatlamayı kaldırarak düz metin oluştur
  const cleanContent = stripHtml(selectedSummary.value.content);

  const summaryText = `
${selectedSummary.value.video_title}
${selectedSummary.value.channel_name}
${formatDate(selectedSummary.value.created_at)}

${cleanContent}

Video URL: ${selectedSummary.value.video_url}
Generated by VecizAI
  `.trim();

  const blob = new Blob([summaryText], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${selectedSummary.value.video_title.slice(0, 50)}_summary.txt`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const shareSummary = async () => {
  if (!selectedSummary.value) return;

  const shareData = {
    title: selectedSummary.value.video_title,
    text: `${selectedSummary.value.video_title}\n\n${truncateSummary(selectedSummary.value.content)}\n\nGenerated by VecizAI`,
    url: selectedSummary.value.video_url
  };

  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`);
      // TODO: Show a toast notification that the content was copied
    }
  } catch (err) {
    console.error('Error sharing:', err);
  }
};

// Feedback Section
const selectedRating = ref(0)
const feedbackComment = ref('')
const activeModalTab = ref('summary')
const isLoadingAudio = ref(false)
const ttsAudio = ref<HTMLAudioElement | null>(null)
const isPlaying = ref(false)
const audioProgress = ref(0)
const currentTime = ref('0:00')
const totalDuration = ref('0:00')
const ttsVolume = ref(1)
const bgVolume = ref(0.01); // 0.02'den 0.01'e düşürüldü (1%)

const submitFeedback = async () => {
  if (!selectedSummary.value) return;
  
  try {
    console.log('Submitting feedback:', {
      rating: selectedRating.value,
      comment: feedbackComment.value
    });

    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    if (!token) {
      console.error('No auth token found');
      throw new Error('Authentication required');
    }

    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/${selectedSummary.value.id}/feedback`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        rating: selectedRating.value,
        comment: feedbackComment.value
      })
    });

    if (!response.ok) {
      throw new Error('Failed to submit feedback');
    }

    // Show success message
    alert(languageStore.t('summaries.feedback.submitSuccess'));
  } catch (error) {
    console.error('Error submitting feedback:', error);
    alert(languageStore.t('errors.feedbackFailed'));
  }
}

const showModal = ref(false)

const handleSummaryClick = (summary: Summary) => {
  selectedSummary.value = summary;
  showModal.value = true;
  loadFeedback(summary.id);
  // Mark as read when opening
  if (!summary.is_read) {
    markSummaryAsRead(summary.id);
  }
}

// Mark summary as read
const markSummaryAsRead = async (summaryId: string) => {
  try {
    const token = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/${summaryId}/read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token.data.session?.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ is_read: true })
    });

    if (!response.ok) {
      throw new Error('Failed to mark summary as read');
    }
    
    // Update local state
    if (summaries.value) {
      const index = summaries.value.findIndex(s => s.id === summaryId);
      if (index !== -1) {
        summaries.value[index].is_read = true;
      }
    }
  } catch (error) {
    console.error('Error marking summary as read:', error);
  }
};

const validateSummary = (summary: Summary) => {
  return summary.id && summary.video_title && summary.content;
};

const shareOnTwitter = (summary: Summary) => {
  const text = `${summary.video_title}\n\n${summary.content}\n\nWatch on YouTube: ${summary.video_url}`;
  const encodedText = encodeURIComponent(text);
  window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
};

const shareOnLinkedIn = (summary: Summary) => {
  const text = `${summary.video_title}\n\n${summary.content}`;
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(summary.video_url);
  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodedText}`, '_blank');
};

// Seçilen kanalın adını göstermek için computed property
const selectedChannelTitle = computed(() => {
  if (selectedChannel.value === 'all') return null
  const channel = channels.value.find(c => c.id === selectedChannel.value)
  return channel ? channel.title : null
})

const forceRender = ref(0)

// Dil değişikliği kontrolü için global değişken
let isLanguageChanging = false;

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const updateAudioProgress = () => {
  if (!ttsAudio.value) return
  
  const audio = ttsAudio.value
  audioProgress.value = (audio.currentTime / audio.duration) * 100
  currentTime.value = formatTime(audio.currentTime)
  totalDuration.value = formatTime(audio.duration)
}

// Arka plan müziği için referans ve değişkenler
const backgroundAudio = ref<HTMLAudioElement | null>(null);

// Listen tabı için audio play ve stop fonksiyonlarını düzenliyorum
const playAudio = () => {
  if (!ttsAudio.value || !backgroundAudio.value) return;
  
  // TTS ses ayarları
  ttsAudio.value.volume = ttsVolume.value;
  
  // Eğer ses baştan başlatılıyorsa (currentTime 0 ise)
  if (ttsAudio.value.currentTime === 0) {
    backgroundAudio.value.currentTime = 0;
    
    // Mobil için düşük, masaüstü için orta seviyeden başla
    const isMobile = window.innerWidth < 640; // Tailwind'in sm breakpoint'i
    
    if (isMobile) {
      // Mobilde düşük ses ile başla
      backgroundAudio.value.volume = bgVolume.value * 0.3;
    } else {
      // Masaüstünde orta seviyeden başla
      backgroundAudio.value.volume = bgVolume.value * 0.5;
    }
  } else {
    // Devam ediyorsa normal ses seviyesini kullan
    backgroundAudio.value.volume = bgVolume.value;
  }
  
  ttsAudio.value.play();
  
  // Fon müziği ayarları
  backgroundAudio.value.loop = true;
  backgroundAudio.value.play();
  
  isPlaying.value = true;
};

const stopAudio = () => {
  if (!ttsAudio.value) return;
  
  ttsAudio.value.pause();
  
  // Arka plan müziğini de durdur
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
  }
  
  isPlaying.value = false;
};

// Audio sonlandığında işlem
const handleTTSEnded = () => {
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
    backgroundAudio.value.currentTime = 0;
  }
  isPlaying.value = false;
};

const textToSpeech = async () => {
  if (!selectedSummary.value?.content) return;
  
  // Debug için bilgi yazdır
  console.log('textToSpeech başlatılıyor, özet ID:', selectedSummary.value.id);
  console.log('TTS URL var mı kontrol ediliyor:', selectedSummary.value?.tts_url);
  console.log('TTS Content var mı kontrol ediliyor:', selectedSummary.value?.tts_content);
  
  const maxRetries = 3;
  let retryCount = 0;
  
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  // İlk işlem öncesi temizlik yap
  if (ttsAudio.value) {
    console.log('Mevcut ttsAudio temizleniyor');
    ttsAudio.value.pause();
    ttsAudio.value.src = '';
    ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
    ttsAudio.value.removeEventListener('ended', handleTTSEnded);
    ttsAudio.value.load();
    ttsAudio.value = null;
  }
  
  const attemptTTS = async () => {
    try {
      isLoadingAudio.value = true;
      console.log(`TTS deneme ${retryCount + 1}/${maxRetries} başlatılıyor`);
      
      if (!selectedSummary.value) {
        console.error('selectedSummary is null, cannot proceed with TTS generation');
        return false;
      }

      // Önce Supabase'den en güncel TTS URL ve içeriğini sorgula
      console.log('Supabase\'den güncel TTS bilgisi sorgulanıyor...');
      
      try {
        if (!selectedSummary.value.summary_id) {
          console.error('summary_id is missing, cannot query Supabase');
        } else {
          const { data, error } = await supabase
            .from('summaries')
            .select('tts_url, tts_content, content, language')
            .eq('id', selectedSummary.value.summary_id)
            .single();
          
          if (error) {
            console.error('Supabase sorgu hatası:', error);
          } else if (data) {
            console.log('Supabase\'den güncel TTS bilgisi alındı:', data);
            
            // Eğer veritabanında güncel bir tts_url varsa, bunu kullan
            if (data.tts_url) {
              console.log('Veritabanından güncel TTS URL bulundu:', data.tts_url);
              
              // Yerel state'i güncelle
              if (selectedSummary.value) {
                selectedSummary.value.tts_url = data.tts_url;
                
                // summaries dizisini de güncelle
                const summaryIndex = summaries.value.findIndex(s => s.summary_id === selectedSummary.value?.summary_id);
                if (summaryIndex !== -1) {
                  summaries.value[summaryIndex].tts_url = data.tts_url;
                }
              }
              
              // API'den ses dosyasını al
              const audioResponse = await fetch(`${import.meta.env.VITE_API_URL}${data.tts_url}`);
              if (audioResponse.ok) {
                const audioBlob = await audioResponse.blob();
                const audioUrl = URL.createObjectURL(audioBlob);
                
                // URL'yi takip listesine ekle
                audioObjectUrls.value.push(audioUrl);
                console.log('Veritabanından alınan güncel TTS için yeni audio URL oluşturuldu:', audioUrl);
                
                // Yeni bir Audio nesnesi oluştur
                ttsAudio.value = new Audio(audioUrl);
                ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
                ttsAudio.value.addEventListener('ended', handleTTSEnded);
                ttsAudio.value.volume = ttsVolume.value;
                
                // Setup background audio if needed
                if (!backgroundAudio.value) {
                  backgroundAudio.value = new Audio('/audio/background.mp3');
                  backgroundAudio.value.loop = true;
                  backgroundAudio.value.volume = bgVolume.value;
                }
                
                console.log('Veritabanından alınan güncel TTS ses dosyası başarıyla yüklendi');
                return true;
              } else {
                console.warn('Veritabanından alınan TTS URL\'sine erişilemedi');
              }
            } else {
              console.log('Veritabanında TTS URL bulunamadı, yerel state kontrol edilecek');
            }
            
            // tts_content değerini de güncelle
            if (data.tts_content && selectedSummary.value) {
              selectedSummary.value.tts_content = data.tts_content;
            }
          }
        }
      } catch (error) {
        console.error('Supabase sorgusunda hata:', error);
      }
      
      // Veritabanı sorgusu başarısız olduysa veya tts_url yoksa, yerel state'e bak
      if (selectedSummary.value?.tts_url) {
        console.log('Yerel state\'te bulunan TTS URL kullanılıyor:', selectedSummary.value.tts_url);
        try {
          const audioResponse = await fetch(`${import.meta.env.VITE_API_URL}${selectedSummary.value.tts_url}`);
          if (audioResponse.ok) {
            const audioBlob = await audioResponse.blob();
            const audioUrl = URL.createObjectURL(audioBlob);
            
            // URL'yi takip listesine ekle
            audioObjectUrls.value.push(audioUrl);
            console.log('Mevcut TTS için yeni audio URL oluşturuldu ve listeye eklendi:', audioUrl);
            
            // Yeni bir Audio nesnesi oluştur
            ttsAudio.value = new Audio(audioUrl);
            ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
            ttsAudio.value.addEventListener('ended', handleTTSEnded);
            ttsAudio.value.volume = ttsVolume.value;
            
            // Setup background audio if needed
            if (!backgroundAudio.value) {
              backgroundAudio.value = new Audio('/audio/background.mp3');
              backgroundAudio.value.loop = true;
              backgroundAudio.value.volume = bgVolume.value;
            }
            
            console.log('Mevcut TTS ses dosyası başarıyla yüklendi');
            return true;
          }
        } catch (error) {
          console.error('Mevcut TTS URL\'sine erişilirken hata:', error);
        }
        console.warn('Mevcut TTS ses dosyasına erişilemedi, yeni TTS oluşturuluyor');
      }

      console.log('TTS URL bulunamadı veya erişilemedi, yeni ses oluşturuluyor');
      console.log(`TTS attempt ${retryCount + 1}/${maxRetries}`);
      
      // İlk olarak, veritabanından summary_id'ye göre tts_content'i doğrudan sorgulayalım
      let ttsText = '';
      try {
        if (!selectedSummary.value.summary_id) {
          console.error('summary_id is missing, cannot query Supabase');
          return false;
        }

        console.log('Supabase\'den TTS içeriği sorgulanıyor. summary_id:', selectedSummary.value.summary_id);
        const { data, error } = await supabase
          .from('summaries')
          .select('tts_content, content')
          .eq('id', selectedSummary.value.summary_id)
          .single();
          
        if (error) {
          console.error('Supabase sorgu hatası:', error);
        } else if (data && data.tts_content) {
          console.log('Supabase\'den TTS içeriği alındı:', data.tts_content.substring(0, 50) + '...');
          ttsText = data.tts_content;
        } else {
          console.log('Supabase\'de TTS içeriği bulunamadı, alternatif kaynaklara bakılıyor');
        }
      } catch (err) {
        console.error('Supabase sorgusu sırasında hata:', err);
      }
      
      // Veritabanından gelen tts_content yoksa, diğer kaynaklara bak
      if (!ttsText) {
        if (selectedSummary.value.tts_content && selectedSummary.value.tts_content.trim().length > 0) {
          console.log('TTS içeriği selectedSummary.tts_content\'ten alınıyor');
          ttsText = selectedSummary.value.tts_content;
        } else {
          console.log('TTS içeriği content\'ten alınıyor (tts_content boş veya yok)');
          ttsText = selectedSummary.value.content;
        }
      }
      
      // İçeriğe teşekkür mesajı ekle (dile göre)
      const thankYouMessage = selectedSummary.value.language === 'tr' 
        ? " Vecizai'yi dinlediğiniz için teşekkürler." 
        : " Thank you for listening Vecizai.";
      
      console.log('TTS içeriğine teşekkür mesajı ekleniyor');
      ttsText = ttsText + thankYouMessage;
      
      // Generate new audio
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tts/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: ttsText,
          languageCode: selectedSummary.value.language === 'tr' ? 'tr-TR' : 'en-US',
          ssmlGender: 'MALE',
          voiceName: selectedSummary.value.language === 'tr' ? 'tr-TR-Wavenet-E' : 'en-US-Wavenet-I',
          videoId: selectedSummary.value.video_id,
          summaryId: selectedSummary.value.id,
          speakingRate: 1.10
        })
      });

      if (!response.ok) {
        throw new Error(`TTS generation error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      // Eğer ses URL'si elde edilirse, summaries dizisini ve seçili özeti güncelle
      if (result.audioUrl) {
        console.log('Yeni TTS URL alındı:', result.audioUrl);
        
        // selectedSummary'yi güncelle
        if (selectedSummary.value) {
          selectedSummary.value.tts_url = result.audioUrl;
        }
        
        // summaries dizisini güncelle
        const summaryIndex = summaries.value.findIndex(s => s && s.id === selectedSummary.value?.id);
        if (summaryIndex !== -1) {
          summaries.value[summaryIndex].tts_url = result.audioUrl;
          console.log('Summaries dizisi TTS URL ile güncellendi');
        }
        
        // Veritabanında da tts_url alanını güncelle
        try {
          if (!selectedSummary.value?.summary_id) {
            console.error('Cannot update Supabase: summary_id is missing');
            return false;
          }

          console.log('Supabase\'de tts_url güncelleniyor...');
          const { error } = await supabase
            .from('summaries')
            .update({ tts_url: result.audioUrl })
            .eq('id', selectedSummary.value.summary_id);
            
          if (error) {
            console.error('Supabase tts_url güncelleme hatası:', error);
          } else {
            console.log('Supabase\'de tts_url başarıyla güncellendi');
          }
        } catch (err) {
          console.error('Supabase güncelleme işleminde hata:', err);
        }
        
        // Fetch the generated audio file
        const audioResponse = await fetch(`${import.meta.env.VITE_API_URL}${result.audioUrl}`);
        if (!audioResponse.ok) {
          throw new Error('Failed to fetch generated audio file');
        }
        
        const audioBlob = await audioResponse.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // URL'yi takip listesine ekle
        audioObjectUrls.value.push(audioUrl);
        console.log('Yeni audio URL oluşturuldu ve listeye eklendi:', audioUrl);
        
        // Her zaman yeni bir Audio nesnesi oluştur
        ttsAudio.value = new Audio(audioUrl);
        ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
        ttsAudio.value.addEventListener('ended', handleTTSEnded);
        ttsAudio.value.volume = ttsVolume.value;
        
        // Setup background audio if needed
        if (!backgroundAudio.value) {
          backgroundAudio.value = new Audio('/audio/background.mp3');
          backgroundAudio.value.loop = true;
          backgroundAudio.value.volume = bgVolume.value;
        }
        
        console.log('Yeni ses dosyası başarıyla oluşturuldu ve yüklendi');
        return true;
      } else if (result.audioContent) {
        // Handle base64 audio content
        const byteCharacters = atob(result.audioContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(blob);
        
        // URL'yi takip listesine ekle
        audioObjectUrls.value.push(audioUrl);
        console.log('Yeni audio URL oluşturuldu ve listeye eklendi:', audioUrl);
        
        // Her zaman yeni bir Audio nesnesi oluştur
        ttsAudio.value = new Audio(audioUrl);
        ttsAudio.value.addEventListener('timeupdate', updateAudioProgress);
        ttsAudio.value.addEventListener('ended', handleTTSEnded);
        ttsAudio.value.volume = ttsVolume.value;
        
        // Setup background audio if needed
        if (!backgroundAudio.value) {
          backgroundAudio.value = new Audio('/audio/background.mp3');
          backgroundAudio.value.loop = true;
          backgroundAudio.value.volume = bgVolume.value;
        }
        
        console.log('Successfully generated and loaded audio content');
        return true;
      } else {
        throw new Error('No audio content received');
      }
    } catch (error) {
      console.error(`TTS attempt ${retryCount + 1} failed:`, error);
      
      // Retry on network errors
      if (error instanceof Error && 
          (error.message.includes('ENETDOWN') || 
           error.message.includes('Failed to fetch') || 
           error.message.includes('NetworkError'))) {
        if (retryCount < maxRetries - 1) {
          retryCount++;
          await delay(2000 * retryCount); // Increasing delay for each retry
          return attemptTTS();
        }
      }
      throw error;
    }
  };
  
  try {
    await attemptTTS();
  } catch (finalError) {
    console.error('All TTS attempts failed:', finalError);
    const errorMsg = languageStore.currentLocale === 'tr' ? 
      'Ses oluşturma sırasında bir hata oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.' : 
      'An error occurred while generating audio. Please check your internet connection and try again.';
    alert(errorMsg);
  } finally {
    isLoadingAudio.value = false;
  }
};

const updateBgVolume = (event: Event) => {
  bgVolume.value = parseFloat((event.target as HTMLInputElement).value);
  
  // Değişikliği anında uygula
  if (backgroundAudio.value) {
    backgroundAudio.value.volume = bgVolume.value;
  }
};

const forceAudioVolumeUpdate = (event: Event) => {
  // Kullanıcı slideri bıraktığında tetiklenir
  // Mobil cihazlarda güvenilir bir şekilde ses değerini uygular
  if (backgroundAudio.value) {
    // Kısa bir gecikme ekleyerek mobil tarayıcıların işlemi tamamlamasına izin ver
    setTimeout(() => {
      backgroundAudio.value!.volume = bgVolume.value;
      console.log('Bg volume set to:', bgVolume.value);
    }, 50);
  }
};

// Watch for volume changes for background audio too
watch(bgVolume, (newVolume) => {
  if (backgroundAudio.value) {
    backgroundAudio.value.volume = newVolume;
  }
});

// Watch for volume changes
watch(ttsVolume, (newVolume) => {
  if (ttsAudio.value) {
    ttsAudio.value.volume = newVolume;
  }
});

// Summary seçildiğinde tab'ı izle ve ayarla
watch(() => selectedSummary.value, (newSummary) => {
  if (newSummary) {
    // Yeni bir özet seçildiğinde her zaman summary tab'ını aktif yap
    activeModalTab.value = 'summary';
    console.log("Seçilen özet değişti, aktif tab 'summary' olarak ayarlandı");
  }
});

// Reset audio on tab change
watch(() => activeModalTab.value, (newTab, oldTab) => {
  console.log(`Tab değişti: ${oldTab} -> ${newTab}`);
  
  if (newTab === 'listen') {
    console.log("Listen tab'ına geçildi, ses durumu sıfırlanıyor");
    // Listen tab'ına geçildiğinde ses durumunu tamamen sıfırla
    if (ttsAudio.value) {
      ttsAudio.value.pause();
      ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
      ttsAudio.value.removeEventListener('ended', handleTTSEnded);
      ttsAudio.value.src = '';
      ttsAudio.value.load();
    }
    
    // Sıfırla
    ttsAudio.value = null;
    isPlaying.value = false;
    audioProgress.value = 0;
    currentTime.value = '0:00';
    totalDuration.value = '0:00';
    
    // TTS URL'lerini önbellekten temizle
    audioObjectUrls.value.forEach(url => {
      try {
        URL.revokeObjectURL(url);
        console.log('Listen tab geçişinde Audio URL önbellekten temizlendi:', url);
      } catch (err) {
        console.error('URL temizlenirken hata:', err);
      }
    });
    audioObjectUrls.value = [];
    
    // Listen tab'ına geçtiğimizde otomatik olarak TTS oluştur
    if (selectedSummary.value?.content) {
      console.log("Listen tab'ına geçildi, otomatik olarak TTS başlatılıyor");
      // Kısa bir gecikme ekleyerek UI güncellemesinin tamamlanmasını bekleyelim
      setTimeout(() => {
        if (!ttsAudio.value && !isLoadingAudio.value) {
          textToSpeech();
        }
      }, 100);
    }
  } else if (oldTab === 'listen') {
    // Listen tab'ından çıkılıyorsa ses durumlarını resetle
    if (ttsAudio.value) {
      ttsAudio.value.pause();
      ttsAudio.value.currentTime = 0;
    }
    if (backgroundAudio.value) {
      backgroundAudio.value.pause();
      backgroundAudio.value.currentTime = 0;
    }
    isPlaying.value = false;
  }
  
  // Transcript tab'ına geçildiğinde ve transcript verisi yoksa, transcript'i getir
  if (newTab === 'transcript' && selectedSummary.value && !selectedSummary.value.transcript) {
    fetchTranscript(selectedSummary.value.id, selectedSummary.value.video_id);
  }
});

// Clean up audio on component unmount
onUnmounted(() => {
  if (ttsAudio.value) {
    ttsAudio.value.removeEventListener('timeupdate', updateAudioProgress);
    ttsAudio.value.removeEventListener('ended', handleTTSEnded);
    ttsAudio.value = null;
  }
  
  if (backgroundAudio.value) {
    backgroundAudio.value.pause();
    backgroundAudio.value = null;
  }
  
  // Önbellekteki tüm URL'leri temizle
  audioObjectUrls.value.forEach(url => {
    try {
      URL.revokeObjectURL(url);
      console.log('Component kaldırılırken audio URL temizlendi:', url);
    } catch (err) {
      console.error('URL temizlenirken hata:', err);
    }
  });
  
  audioObjectUrls.value = [];
});

// Transcript almak için yeni fonksiyon
const fetchTranscript = async (summaryId: string, videoId: string) => {
  if (!summaryId || !videoId) return;
  const summary = summaries.value.find(s => s.id === summaryId);
  if (!summary?.language) {
    console.error('Transcript fetch: Summary language is missing!');
    isLoadingTranscript.value = false;
    return;
  }
  const language = summary.language;
  isLoadingTranscript.value = true;
  try {
    const token = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcripts/${videoId}?language=${language}`, {
      headers: {
        'Authorization': `Bearer ${token.data.session?.access_token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch transcript');
    }

    const data = await response.json();
    console.log('Transcript API Response:', data);

    // Veriyi daha detaylı inceleyelim
    if (data) {
      let transcriptContent = null;
      
      // API yanıtının yapısını kontrol edelim
      if (data.transcript) {
        console.log('Transcript data.transcript içinde bulundu');
        transcriptContent = data.transcript;
        
        // JSON meta verilerini temizlemeye çalış
        try {
          // Eğer JSON objesiyse
          if (typeof transcriptContent === 'string' && (transcriptContent.startsWith('{') || transcriptContent.includes('"formatted_text":'))) {
            const jsonObj = JSON.parse(transcriptContent);
            if (jsonObj.formatted_text) {
              transcriptContent = jsonObj.formatted_text;
              console.log('JSON meta verileri temizlendi, formatted_text içeriği alındı');
            }
          }
        } catch (e) {
          console.warn('JSON ayrıştırma hatası, orijinal transcript kullanılacak:', e);
        }
      } 
      // Doğrudan string olarak gelmiş olabilir
      else if (typeof data === 'string') {
        console.log('Transcript string olarak bulundu');
        transcriptContent = data;
        
        // JSON meta verilerini temizlemeye çalış
        try {
          if (data.startsWith('{') || data.includes('"formatted_text":')) {
            const jsonObj = JSON.parse(data);
            if (jsonObj.formatted_text) {
              transcriptContent = jsonObj.formatted_text;
              console.log('JSON meta verileri temizlendi, formatted_text içeriği alındı');
            }
          }
        } catch (e) {
          console.warn('JSON ayrıştırma hatası, orijinal transcript kullanılacak:', e);
        }
      } 
      // API yanıtının kendisi JSON objesi olabilir
      else if (Object.keys(data).length > 0) {
        console.log('Transcript API yanıtında anahtar alanlar bulundu');
        
        // Önce formatted_text'i kontrol et
        if (data.formatted_text) {
          transcriptContent = data.formatted_text;
          console.log('formatted_text alanı bulundu');
        }
        // Ardından içerikteki text alanını kontrol et
        else if (data.text) {
          transcriptContent = data.text;
          console.log('text alanı bulundu');
        }
        // Ardından content alanını kontrol et
        else if (data.content) {
          transcriptContent = data.content;
          console.log('content alanı bulundu');
        }
        // Son çare olarak veriyi olduğu gibi kullan
        else {
          transcriptContent = JSON.stringify(data);
          console.log('uygun alan bulunamadı, tüm veri string olarak kullanılacak');
        }
      }
      
      // Eğer transcript içeriği bulduysak kaydet
      if (transcriptContent) {
        console.log('Transcript başarıyla işlendi');
        
        // selectedSummary'yi güncelle
        if (selectedSummary.value && selectedSummary.value.id === summaryId) {
          selectedSummary.value.transcript = transcriptContent;
        }
        
        // summaries dizisini güncelle
        const summaryIndex = summaries.value.findIndex(s => s.id === summaryId);
        if (summaryIndex !== -1) {
          summaries.value[summaryIndex].transcript = transcriptContent;
        }
      } else {
        console.warn('API transcript verisi döndürdü ancak içerik ayrıştırılamadı:', data);
      }
    } else {
      console.warn('API transcript verisi döndürmedi');
    }
  } catch (err) {
    console.error('Error fetching transcript:', err);
  } finally {
    isLoadingTranscript.value = false;
  }
};

// Feedback'i özet bazında yükle
const loadFeedback = async (summaryId: string) => {
  try {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) {
      selectedRating.value = 0;
      feedbackComment.value = '';
      return;
    }
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/summaries/${summaryId}/feedback`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      selectedRating.value = 0;
      feedbackComment.value = '';
      return;
    }
    const data = await response.json();
    selectedRating.value = data.rating || 0;
    feedbackComment.value = data.comment || '';
  } catch (error) {
    selectedRating.value = 0;
    feedbackComment.value = '';
  }
};

watch(showModal, (val) => {
  if (!val) {
    selectedRating.value = 0;
    feedbackComment.value = '';
  }
});
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20 pb-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Main Content -->
      <div class="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <h1 class="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-transparent bg-clip-text">
            {{ languageStore.t('summaries.title') }}
          </h1>

          <!-- Navigation Tabs -->
          <div class="flex items-center gap-2 bg-gradient-to-br from-gray-50/80 to-white/80 p-1.5 rounded-xl backdrop-blur-sm border border-gray-100/50 shadow-sm">
            <router-link
              to="/channels"
              class="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-indigo-600 transition-all duration-300 text-sm rounded-lg hover:bg-white relative group/nav overflow-hidden"
            >
              <span class="absolute inset-0 bg-gradient-to-r from-indigo-50 to-purple-50 opacity-0 group-hover/nav:opacity-100 transition-opacity"></span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 relative text-indigo-500 group-hover/nav:rotate-6 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="relative font-medium">{{ languageStore.t('navigation.channels') }}</span>
            </router-link>
            <router-link
              to="/"
              class="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-indigo-600 transition-all duration-300 text-sm rounded-lg hover:bg-white relative group/nav overflow-hidden"
            >
              <span class="absolute inset-0 bg-gradient-to-r from-indigo-50 to-purple-50 opacity-0 group-hover/nav:opacity-100 transition-opacity"></span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 relative text-purple-500 group-hover/nav:rotate-6 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
              </svg>
              <span class="relative font-medium">{{ languageStore.t('navigation.home') }}</span>
            </router-link>
          </div>
        </div>

        <!-- Filter Tabs -->
        <div class="flex flex-wrap gap-2 sm:gap-4 mb-6">
          <!-- Selected Channel Title -->
          <div v-if="selectedChannelTitle" class="w-full mb-2 bg-indigo-50 p-3 rounded-lg border-l-4 border-indigo-500">
            <div class="flex items-center justify-between">
              <h3 class="text-indigo-700 font-medium">
                {{ selectedChannelTitle }}
              </h3>
              <button 
                @click="selectedChannel = 'all'" 
                class="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1 bg-white rounded-md hover:shadow-sm transition-all"
              >
                {{ languageStore.t('summaries.channels.all') }}
              </button>
            </div>
          </div>
          
          <!-- Time Filter Buttons -->
          <div class="flex flex-wrap gap-1.5 sm:gap-2">
            <button
              v-for="tab in ['all', 'today', 'week', 'month']"
              :key="tab"
              @click="activeTab = tab"
              class="px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-300 relative group/filter overflow-hidden"
              :class="[
                activeTab === tab
                  ? 'text-white shadow-md'
                  : 'text-gray-600 hover:text-indigo-600 bg-gray-50 hover:bg-white'
              ]"
            >
              <span 
                v-if="activeTab === tab" 
                class="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600"
              ></span>
              <span 
                v-else
                class="absolute inset-0 bg-gradient-to-r from-indigo-50 to-purple-50 opacity-0 group-hover/filter:opacity-100 transition-opacity"
              ></span>
              <span class="relative">{{ languageStore.t(`summaries.filters.${tab}`) }}</span>
            </button>
          </div>

          <!-- Language Filter -->
          <div class="flex items-center gap-1.5 sm:gap-2">
            <span class="text-xs sm:text-sm text-gray-500">{{ languageStore.t('summaries.language') }}:</span>
            <div class="flex gap-1 bg-gray-50 p-1 rounded-lg">
              <button
                v-for="lang in ['all', 'tr', 'en']"
                :key="lang"
                @click="selectedLanguage = lang"
                class="px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-300 relative group/lang"
                :class="[
                  selectedLanguage === lang
                    ? 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-indigo-600 hover:bg-white'
                ]"
              >
                {{ languageStore.t(`summaries.languages.${lang}`) }}
              </button>
            </div>
          </div>
          
          <!-- Channel Filter Dropdown -->
          <div class="flex items-center gap-1.5 sm:gap-2">
            <span class="text-xs sm:text-sm text-gray-500">{{ languageStore.t('summaries.channels.title') }}:</span>
            <select 
              v-model="selectedChannel"
              class="px-3 py-1.5 bg-gray-50 rounded-lg text-xs sm:text-sm font-medium border border-gray-200 hover:border-indigo-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-all duration-300"
            >
              <option value="all">{{ languageStore.t('summaries.channels.all') }}</option>
              <option v-for="channel in channels" :key="channel.id" :value="channel.id">
                {{ channel.title }}
              </option>
            </select>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <div class="w-16 h-16 mx-auto mb-4 relative">
            <div class="absolute inset-0 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
            <div class="absolute inset-2 rounded-full border-t-2 border-b-2 border-purple-500 animate-spin"></div>
            <div class="absolute inset-4 rounded-full border-t-2 border-b-2 border-pink-500 animate-spin"></div>
          </div>
          <p class="mt-4 text-gray-600 animate-pulse">{{ languageStore.t('common.loading') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="bg-red-50 p-4 rounded-xl">
            <p class="text-red-600">{{ error }}</p>
          </div>
        </div>

        <!-- Summaries Grid -->
        <div v-else-if="filteredSummaries.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="summary in filteredSummaries" 
               :key="`${summary.id}-${forceRender}`"
               @click="handleSummaryClick(summary)"
               class="group bg-gradient-to-br from-white to-gray-50/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer">
            <!-- Summary Header -->
            <div class="relative pb-[56.25%]">
              <img 
                :src="summary.video_thumbnail"
                :alt="summary.video_title"
                class="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
              <div class="absolute bottom-0 left-0 right-0 p-4">
                <h3 class="text-lg font-semibold text-white line-clamp-2 drop-shadow-sm group-hover:text-indigo-200 transition-colors">
                  {{ summary.video_title }}
                </h3>
              </div>
            </div>

            <!-- Summary Info -->
            <div class="p-4">
              <div class="flex flex-col gap-4">
                <!-- Channel Info -->
                <div class="flex items-center gap-2">
                  <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10 flex items-center justify-center text-indigo-600 font-medium group-hover:scale-110 transition-transform">
                    {{ summary.channel_name?.[0]?.toUpperCase() || '?' }}
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900">{{ summary.channel_name }}</p>
                    <p class="text-xs text-gray-500">{{ formatDate(summary.created_at) }}</p>
                  </div>
                </div>

                <!-- Summary Preview -->
                <p class="text-sm text-gray-600 line-clamp-3">
                  {{ truncateSummary(summary.content || '') }}
                </p>

                <!-- Status Tags -->
                <div class="flex flex-wrap gap-2">
                  <span class="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-lg">
                    {{ summary.language?.toUpperCase() || 'N/A' }}
                  </span>
                  <span 
                    class="px-2 py-1 text-xs font-medium rounded-lg"
                    :class="summary.is_read ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'"
                  >
                    {{ summary.is_read ? languageStore.t('summaries.status.read') : languageStore.t('summaries.status.unread') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- No Summaries After Filtering -->
        <div v-else-if="hasNoSummaries" class="text-center py-12">
          <div class="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-800 mb-2">{{ languageStore.t('summaries.noSummaries') }}</h3>
          
          <!-- Show special message when a channel is selected but has no summaries -->
          <p v-if="selectedChannel !== 'all'" class="text-gray-600 mb-6">
            {{ languageStore.t('summaries.channels.noSummariesForChannel') }}
          </p>
          <p v-else class="text-gray-600 mb-6">
            {{ languageStore.t('summaries.noSummaries') }}
          </p>
          
          <router-link to="/channels" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ languageStore.t('summaries.channels.addChannel') }}</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Summary Modal -->
    <div
      v-if="selectedSummary"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]"
      @click.self="closeSummary"
    >
      <div
        class="bg-white rounded-xl shadow-lg w-full max-h-[90vh] overflow-y-auto mx-auto relative"
        style="max-width: min(90vw, 640px);"
        @click.stop
      >
        <div class="p-4 sm:p-6">
          <!-- Header -->
          <div class="flex items-center justify-between gap-4 mb-4 sticky top-0 bg-white z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 border-b border-gray-100">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-medium">
                {{ selectedSummary.channel_name[0].toUpperCase() }}
              </div>
              <div>
                <h3 class="font-semibold text-gray-900">{{ selectedSummary.channel_name }}</h3>
                <p class="text-sm text-gray-500">{{ formatDate(selectedSummary.created_at) }}</p>
              </div>
            </div>
            <button
              @click="closeSummary"
              class="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Video Player -->
          <div 
            class="mb-6 rounded-lg overflow-hidden bg-gray-100 mx-auto" 
            style="max-width: min(100%, 480px); aspect-ratio: 16/10;"
          >
            <iframe
              v-if="selectedSummary?.video_url && getEmbedUrl(selectedSummary.video_url)"
              :src="getEmbedUrl(selectedSummary.video_url)"
              class="w-full h-full"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>

          <!-- Video Title -->
          <h2 class="text-lg sm:text-xl font-bold text-gray-900 mb-4">{{ selectedSummary.video_title }}</h2>

          <!-- Tab Controls -->
          <div class="flex overflow-x-auto mb-4 bg-gray-50 p-1 rounded-xl">
            <button
              v-for="tab in ['summary', 'transcript', 'listen']"
              :key="tab"
              @click="activeModalTab = tab"
              class="flex-1 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200"
              :class="{ 'bg-white shadow-md text-indigo-600 scale-[1.02]': activeModalTab === tab, 'text-gray-600 hover:text-indigo-600 hover:bg-white/50': activeModalTab !== tab }"
            >
              <span class="flex items-center justify-center gap-2">
                <svg v-if="tab === 'summary'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <svg v-else-if="tab === 'transcript'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <svg v-else-if="tab === 'listen'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M12 18.364a7 7 0 010-12.728M8.464 15.536a5 5 0 010-7.072m12.02 4.072a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="hidden sm:inline">{{ languageStore.t(`home.tabs.${tab}`) }}</span>
                <span class="sm:hidden">{{ languageStore.t(`home.tabs.${tab}Short`) }}</span>
              </span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="bg-gray-50 rounded-xl p-6">
            <!-- Summary Tab -->
            <div v-if="activeModalTab === 'summary'" class="prose prose-sm sm:prose max-w-none">
              <div class="summary-content" v-html="formattedSummary"></div>
            </div>

            <!-- Transcript Tab -->
            <div v-if="activeModalTab === 'transcript'" class="p-4">
              <!-- Yükleme durumu -->
              <div v-if="isLoadingTranscript" class="flex flex-col items-center justify-center py-8">
                <div class="spinner animate-spin h-8 w-8 border-4 border-indigo-500 rounded-full border-t-transparent mb-4"></div>
                <p class="text-sm text-gray-500">{{ languageStore.t('home.transcript.loading') }}</p>
              </div>
              <!-- Transcript varsa göster -->
              <div v-else-if="selectedSummary?.transcript" class="prose prose-sm max-w-none">
                <div class="whitespace-pre-line text-gray-700">{{ selectedSummary.transcript }}</div>
              </div>
              <!-- Transcript yoksa mesaj göster -->
              <div v-else class="text-center py-8">
                <div class="text-gray-500">{{ languageStore.t('home.transcript.empty') }}</div>
              </div>
            </div>

            <!-- Listen Tab -->
            <div v-else-if="activeModalTab === 'listen'" class="h-full">
              <div v-if="!selectedSummary.content" class="text-center py-8">
                <p class="text-gray-500">{{ languageStore.t('home.listen.noSummary') }}</p>
              </div>
              <div v-else>
                <div class="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 relative overflow-hidden">
                  <div class="absolute inset-0 bg-grid-gray-100 opacity-[0.05]"></div>
                  <div class="relative">
                    <div class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-full flex items-center justify-center group">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-indigo-500 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M12 18.364a7 7 0 010-12.728M8.464 15.536a5 5 0 010-7.072m12.02 4.072a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p class="text-gray-600 mb-6">{{ languageStore.t('home.listen.description') }}</p>
                    <div v-if="isLoadingAudio" class="flex flex-col items-center justify-center mb-4">
                      <div class="spinner animate-spin h-8 w-8 border-4 border-indigo-500 rounded-full border-t-transparent mb-2"></div>
                      <p class="text-sm text-gray-500">{{ languageStore.t('home.listen.loading') }}</p>
                    </div>
                    
                    <!-- Audio Controls -->
                    <div v-if="ttsAudio" class="space-y-4">
                      <!-- Play/Stop Buttons -->
                      <div class="flex justify-center gap-4">
                        <button 
                          v-if="!isPlaying"
                          @click="playAudio"
                          class="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
                          </svg>
                          <span>{{ ttsAudio?.currentTime === 0 ? languageStore.t('home.listen.playButton') : languageStore.t('home.listen.playAgainButton') }}</span>
                        </button>
                        <button 
                          v-else
                          @click="stopAudio"
                          class="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1zm4 0a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                          </svg>
                          <span>{{ languageStore.t('home.listen.stopButton') }}</span>
                        </button>
                      </div>
                      
                      <!-- Progress Bar and Time -->
                      <div class="space-y-2">
                        <div class="flex items-center justify-between text-sm text-gray-600 mb-1">
                          <span>{{ currentTime }}</span>
                          <span>{{ totalDuration }}</span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            class="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                            :style="{ width: audioProgress + '%' }"
                          ></div>
                        </div>
                      </div>
                      
                      <!-- Volume Control -->
                      <div class="space-y-2 hidden sm:block">
                        <div class="flex items-center gap-2">
                          <span class="text-sm text-gray-600">{{ languageStore.t('home.listen.ttsVolume') }}:</span>
                          <input 
                            type="range" 
                            v-model="ttsVolume" 
                            min="0" 
                            max="1" 
                            step="0.1"
                            class="w-full"
                          />
                        </div>
                        <div class="flex items-center gap-2">
                          <span class="text-sm text-gray-600">{{ languageStore.t('home.listen.bgVolume') }}:</span>
                          <input 
                            type="range" 
                            :value="bgVolume" 
                            @input="updateBgVolume($event)" 
                            @change="forceAudioVolumeUpdate($event)"
                            min="0" 
                            max="1" 
                            step="0.1"
                            class="w-full h-6 mobile-range-input appearance-none bg-gray-200 rounded-lg overflow-hidden"
                          />
                        </div>
                      </div>
                    </div>

                    <button 
                      v-if="!ttsAudio"
                      @click="textToSpeech" 
                      :disabled="isLoadingAudio || !selectedSummary.content"
                      class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg transition-colors"
                      :class="isLoadingAudio || !selectedSummary.content ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'"
                    >
                      <span>{{ languageStore.t('home.listen.generateButton') }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Feedback Section -->
          <div class="feedback-section mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 class="text-lg font-semibold mb-3">{{ languageStore.t('summaries.feedback.title') }}</h3>
            
            <!-- Rating -->
            <div class="mb-4">
              <p class="text-sm text-gray-600 mb-2">
                {{ selectedRating > 0 
                  ? languageStore.t('summaries.feedback.rating.rated').replace('{rating}', String(selectedRating))
                  : languageStore.t('summaries.feedback.rating.notRated')
                }}
              </p>
              <div class="flex gap-1">
                <button
                  v-for="i in 5"
                  :key="i"
                  @click="selectedRating = i"
                  class="text-2xl"
                  :class="i <= selectedRating ? 'text-yellow-400' : 'text-gray-300'"
                >
                  ★
                </button>
              </div>
            </div>

            <!-- Comment -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ languageStore.t('summaries.feedback.comment.label') }}
              </label>
              <textarea
                v-model="feedbackComment"
                rows="3"
                class="w-full p-2 border rounded-md"
                :placeholder="languageStore.t('summaries.feedback.comment.placeholder')"
              ></textarea>
            </div>

            <!-- Submit Button -->
            <button
              @click="submitFeedback"
              class="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              {{ languageStore.t('summaries.feedback.submit') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.summary-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.6;
  color: #374151;
}

.summary-plain-text {
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.6;
  color: #374151;
  font-size: 1rem;
}

.summary-plain-text strong {
  color: #4338ca;
  font-weight: 600;
}

.summary-plain-text a {
  color: #2563eb;
  text-decoration: underline;
}

.summary-content p {
  margin-bottom: 1rem;
}

.summary-content h1, 
.summary-content h2, 
.summary-content h3 {
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #111827;
}

.summary-content h1 { font-size: 1.5rem; }
.summary-content h2 { font-size: 1.25rem; }
.summary-content h3 { font-size: 1.125rem; }

.summary-content strong {
  color: #4338ca;
  font-weight: 600;
}

.summary-content em {
  color: #0369a1;
  font-style: italic;
}

/* Emojiler için animasyon */
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

/* Yeni CSS Sınıfları */
.summary-content .section-heading {
  background: linear-gradient(90deg, rgba(67, 56, 202, 0.07), transparent);
  border-left: 3px solid #4338ca;
  padding: 0.5rem;
  margin: 1rem 0;
  border-radius: 0.25rem;
}

.summary-content .section-heading-text {
  font-size: 1.1em;
  font-weight: 700;
  color: #4338ca;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.summary-content .section-emoji {
  font-size: 1.4em;
  margin-right: 0.4rem;
  display: inline-block;
  animation: pulse 2s infinite ease-in-out;
}

.summary-content .bullet-item {
  display: flex;
  margin-bottom: 0.75rem;
}

.summary-content .simple-bullet {
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;
}

.summary-content .simple-bullet:before {
  content: "•";
  position: absolute;
  left: 0.5rem;
  color: #4f46e5;
  font-weight: bold;
}

.summary-content .bullet-emoji {
  font-size: 1.2em;
  min-width: 1.5rem;
  margin-right: 0.5rem;
}

.summary-content .bullet-content {
  flex: 1;
}

.summary-content .subsection-heading {
  font-weight: 600;
  font-size: 1.1em;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
  color: #4f46e5;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.25rem;
}

.summary-content .quote-highlight {
  font-style: italic;
  color: #0369a1;
  background-color: #e0f2fe;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
}

.summary-content .highlight-critical {
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.summary-content .highlight-concept {
  background-color: #e0e7ff;
  color: #4338ca;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.summary-content .highlight-advice {
  background-color: #d1fae5;
  color: #047857;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.summary-content .highlight-warning {
  background-color: #fef3c7;
  color: #92400e;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.summary-content .highlight-default {
  font-weight: 600;
  color: #4338ca;
}

.summary-content .stat-highlight {
  font-weight: 700;
  color: #0891b2;
  background-color: #cffafe;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
}

.summary-content .section-divider {
  border-top: 1px dashed #d1d5db;
  margin: 1.5rem 0;
  height: 0;
}

.summary-content .conclusion-box {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #4f46e5;
  padding: 1rem;
  margin: 1.5rem 0;
  border-radius: 0.375rem;
}

.summary-content .conclusion-heading {
  font-weight: 600;
  color: #4f46e5;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.summary-content .conclusion-emoji {
  font-size: 1.25em;
  margin-right: 0.5rem;
}

.summary-content .conclusion-text {
  margin: 0;
}

.summary-content .connector-text {
  color: #4f46e5;
  font-weight: 600;
}

.summary-content .note-box,
.summary-content .tip-box,
.summary-content .warning-box {
  padding: 0.75rem;
  margin: 1rem 0;
  border-radius: 0.25rem;
}

.summary-content .note-box {
  background-color: #e4e4e7;
  border-left: 4px solid #71717a;
}

.summary-content .tip-box {
  background-color: #dbeafe;
  border-left: 4px solid #3b82f6;
}

.summary-content .warning-box {
  background-color: #fee2e2;
  border-left: 4px solid #ef4444;
}

.summary-content .note-heading,
.summary-content .tip-heading,
.summary-content .warning-heading {
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
}

.summary-content .tip-heading {
  color: #2563eb;
}

.summary-content .warning-heading {
  color: #b91c1c;
}

.summary-content .note-emoji,
.summary-content .tip-emoji,
.summary-content .warning-emoji {
  font-size: 1.1em;
  margin-right: 0.5rem;
}

.summary-content .note-content,
.summary-content .tip-content,
.summary-content .warning-content {
  margin: 0;
}

/* Yeni eklenen highlight stilleri */
.summary-plain-text .highlight-concept {
  background-color: #e0e7ff;
  color: #4338ca;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.summary-plain-text .highlight-concept:hover {
  background-color: #c7d2fe;
  transform: translateY(-1px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.summary-plain-text .stat-highlight {
  font-weight: 700;
  color: #0891b2;
  background-color: #cffafe;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.summary-plain-text .stat-highlight:hover {
  background-color: #a5f3fc;
  transform: translateY(-1px);
}

.summary-plain-text .quote-highlight {
  font-style: italic;
  color: #0369a1;
  background-color: #e0f2fe;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  border-left: 2px solid #0369a1;
  margin: 0 0.25rem;
  transition: all 0.2s ease;
}

.summary-plain-text .quote-highlight:hover {
  background-color: #bae6fd;
}

.summary-plain-text .highlight-warning {
  background-color: #fef3c7;
  color: #92400e;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
  border-left: 2px solid #d97706;
  transition: all 0.2s ease;
}

.summary-plain-text .highlight-warning:hover {
  background-color: #fde68a;
}

.summary-plain-text .highlight-advice {
  background-color: #d1fae5;
  color: #047857;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
  border-left: 2px solid #059669;
  transition: all 0.2s ease;
}

.summary-plain-text .highlight-advice:hover {
  background-color: #a7f3d0;
}

.summary-plain-text .simple-bullet {
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;
}

.summary-plain-text .simple-bullet:before {
  content: "•";
  position: absolute;
  left: 0.5rem;
  color: #4f46e5;
  font-weight: bold;
}

/* Bölüm Başlıkları */
.summary-plain-text .section-heading {
  background: linear-gradient(90deg, rgba(67, 56, 202, 0.07), transparent);
  border-left: 3px solid #4338ca;
  padding: 0.75rem 0.5rem;
  margin: 1.5rem 0 1rem 0;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.summary-plain-text .section-heading:hover {
  background: linear-gradient(90deg, rgba(67, 56, 202, 0.12), rgba(99, 102, 241, 0.05));
  transform: translateX(2px);
}

.summary-plain-text .section-heading-text {
  font-size: 1.1em;
  font-weight: 700;
  color: #4338ca;
  display: flex;
  align-items: center;
}

.summary-plain-text .section-emoji {
  font-size: 1.4em;
  margin-right: 0.6rem;
  display: inline-block;
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}
</style> 